import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BellRing, Mail, MessageCircle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Toggle = ({ on, onClick, testId, label, icon: Icon, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    aria-pressed={on}
    className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-start transition-colors disabled:opacity-40 ${
      on
        ? 'border-blue-300 dark:border-blue-400/40 bg-blue-50/70 dark:bg-blue-500/10'
        : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5'
    }`}
  >
    <span className="flex items-center gap-2.5 min-w-0">
      <Icon className={`w-4 h-4 shrink-0 ${on ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
      <span className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{label}</span>
    </span>
    <span
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
        on ? 'bg-blue-600' : 'bg-gray-300 dark:bg-white/15'
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
          on ? 'start-[1.125rem]' : 'start-0.5'
        }`}
      />
    </span>
  </button>
);

export const ReminderPreferences = ({ token }) => {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/profile/reminder-preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setPrefs(res.data))
      .catch(() => setPrefs({ enabled: true, email: true, whatsapp: true }));
  }, [token]);

  const save = async (next) => {
    setPrefs(next);
    setError('');
    try {
      await axios.put(`${API}/profile/reminder-preferences`, next, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setError(t('reminders.saveError', 'Could not save reminder settings'));
    }
  };

  if (!prefs) return null;

  return (
    <div
      data-testid="reminder-preferences-card"
      className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6"
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <span className="grid place-items-center w-11 h-11 shrink-0 rounded-2xl bg-amber-50 dark:bg-amber-500/10">
          <BellRing className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </span>
        <div className="min-w-[12rem] flex-1">
          <h3 className="font-display font-bold text-gray-900 dark:text-white">
            {t('reminders.title', 'Appointment reminders')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('reminders.subtitle', "We'll remind you 24 hours and 2 hours before each appointment.")}
          </p>
        </div>
        {saved && (
          <span
            data-testid="reminder-saved-badge"
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300"
          >
            <Check className="w-3 h-3" />
            {t('reminders.saved', 'Reminder settings saved')}
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Toggle
          on={prefs.enabled}
          onClick={() => save({ ...prefs, enabled: !prefs.enabled })}
          testId="reminder-toggle-enabled"
          label={t('reminders.enabled', 'Reminders on')}
          icon={BellRing}
        />
        <Toggle
          on={prefs.enabled && prefs.email}
          disabled={!prefs.enabled}
          onClick={() => save({ ...prefs, email: !prefs.email })}
          testId="reminder-toggle-email"
          label={t('reminders.email', 'Email')}
          icon={Mail}
        />
        <Toggle
          on={prefs.enabled && prefs.whatsapp}
          disabled={!prefs.enabled}
          onClick={() => save({ ...prefs, whatsapp: !prefs.whatsapp })}
          testId="reminder-toggle-whatsapp"
          label={t('reminders.whatsapp', 'WhatsApp')}
          icon={MessageCircle}
        />
      </div>

      {error && (
        <p className="mt-3 text-sm text-rose-500" data-testid="reminder-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default ReminderPreferences;
